import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { List, X, Gear, SignOut } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();
    const { isAuthenticated, isAdmin, logout } = useAuth();

    const navLinks = [
        { href: '/#about', label: 'About' },
        { href: '/#software', label: 'Software' },
        { href: '/#contact', label: 'Contact' },
    ];

    const handleLogout = async () => {
        await logout();
        window.location.href = '/';
    };

    return (
        <nav 
            className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5"
            data-testid="navbar"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16 sm:h-20">
                    {/* Logo */}
                    <Link 
                        to="/" 
                        className="flex items-center gap-3 group"
                        data-testid="logo-link"
                    >
                        <img 
                            src="https://customer-assets.emergentagent.com/job_eng-software-hub/artifacts/3icn5ytz_Spindrift%20Engineering%20Software.png"
                            alt="Spindrift Engineering"
                            className="h-10 sm:h-12 w-auto"
                        />
                        <div className="hidden sm:block">
                            <span className="text-lg font-semibold text-text-primary tracking-tight">
                                Spindrift Engineering
                            </span>
                        </div>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-8">
                        {navLinks.map((link) => (
                            <a
                                key={link.href}
                                href={link.href}
                                className="nav-link text-text-secondary hover:text-text-primary transition-colors text-sm font-medium"
                                data-testid={`nav-${link.label.toLowerCase()}`}
                            >
                                {link.label}
                            </a>
                        ))}
                        
                        {isAuthenticated && isAdmin ? (
                            <div className="flex items-center gap-4">
                                <Link
                                    to="/admin"
                                    className="flex items-center gap-2 text-secondary hover:text-primary transition-colors text-sm font-medium"
                                    data-testid="nav-admin"
                                >
                                    <Gear size={18} weight="duotone" />
                                    Admin
                                </Link>
                                <button
                                    onClick={handleLogout}
                                    className="flex items-center gap-2 text-text-muted hover:text-text-primary transition-colors text-sm font-medium"
                                    data-testid="nav-logout"
                                >
                                    <SignOut size={18} weight="duotone" />
                                    Logout
                                </button>
                            </div>
                        ) : (
                            <Link
                                to="/admin/login"
                                className="text-text-muted hover:text-text-primary transition-colors text-sm"
                                data-testid="nav-login"
                            >
                                Admin
                            </Link>
                        )}
                    </div>

                    {/* Mobile menu button */}
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className="md:hidden p-2 text-text-secondary hover:text-text-primary transition-colors"
                        data-testid="mobile-menu-toggle"
                    >
                        {isOpen ? <X size={24} /> : <List size={24} />}
                    </button>
                </div>
            </div>

            {/* Mobile Navigation */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="md:hidden glass border-t border-white/5"
                        data-testid="mobile-menu"
                    >
                        <div className="px-4 py-4 space-y-3">
                            {navLinks.map((link) => (
                                <a
                                    key={link.href}
                                    href={link.href}
                                    onClick={() => setIsOpen(false)}
                                    className="block py-2 text-text-secondary hover:text-text-primary transition-colors text-sm font-medium"
                                    data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                                >
                                    {link.label}
                                </a>
                            ))}
                            
                            {isAuthenticated && isAdmin ? (
                                <>
                                    <Link
                                        to="/admin"
                                        onClick={() => setIsOpen(false)}
                                        className="flex items-center gap-2 py-2 text-secondary hover:text-primary transition-colors text-sm font-medium"
                                        data-testid="mobile-nav-admin"
                                    >
                                        <Gear size={18} weight="duotone" />
                                        Admin Dashboard
                                    </Link>
                                    <button
                                        onClick={handleLogout}
                                        className="flex items-center gap-2 py-2 text-text-muted hover:text-text-primary transition-colors text-sm font-medium w-full text-left"
                                        data-testid="mobile-nav-logout"
                                    >
                                        <SignOut size={18} weight="duotone" />
                                        Logout
                                    </button>
                                </>
                            ) : (
                                <Link
                                    to="/admin/login"
                                    onClick={() => setIsOpen(false)}
                                    className="block py-2 text-text-muted hover:text-text-primary transition-colors text-sm"
                                    data-testid="mobile-nav-login"
                                >
                                    Admin Login
                                </Link>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
};

export default Navbar;
